/**
 * The stages of a registration modal, and nothing of what they hold.
 *
 * Which stage is on screen, which tabs can be reached, which footer controls belong
 * to it, and the inline error a stage shows when it refuses to move on. What a stage
 * asks, and what has to be true before the next one opens, is the door's: the forum
 * checks an organization type, the plans door a product.
 *
 * The markup is drawn by <x-registration.wizard> and <x-registration.wizard.stage>:
 *
 *   [data-stage="name"]         a stage, in the order they are drawn
 *   [data-tab="name"]           its tab, drawn only when there is more than one stage
 *   [data-wizard-foot="name"]   a footer control shown only on that stage
 *   [data-wizard-goto="name"]   a control that opens that stage
 *
 * A tab is reachable up to the stage on screen and locked beyond it, so a later
 * stage opens only through the door's own Continue.
 *
 * Every change of stage is announced on the modal as `wizard:stage`, with the stage in
 * detail.stage, for a door that shows something about where the visitor is.
 *
 * @param {HTMLElement} modal the .lk-modal-backdrop
 */
export function wizard(modal) {
    const body = modal.querySelector('.lk-modal-body');
    const stages = Array.from(modal.querySelectorAll('[data-stage]')).map((section) => section.dataset.stage);
    let current = stages[0] || null;

    function clearError() {
        modal.querySelectorAll('.lk-wizard__error').forEach((element) => element.remove());
    }

    /** One message at a time, placed after what it is about. */
    function showError(anchor, message) {
        clearError();

        if (! anchor || ! message) {
            return;
        }

        const element = document.createElement('p');
        element.className = 'lk-wizard__error';
        element.setAttribute('role', 'alert');
        element.textContent = message;
        anchor.insertAdjacentElement('afterend', element);
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    function go(name) {
        const position = stages.indexOf(name);

        if (position === -1) {
            return;
        }

        stages.forEach((stage, index) => {
            modal.querySelector(`[data-stage="${stage}"]`).classList.toggle('is-active', stage === name);

            const tab = modal.querySelector(`[data-tab="${stage}"]`);

            if (tab) {
                tab.classList.toggle('is-active', stage === name);
                tab.classList.toggle('is-locked', index > position);
            }
        });

        modal.querySelectorAll('[data-wizard-foot]').forEach((control) => {
            control.hidden = control.dataset.wizardFoot !== name;
        });

        current = name;
        clearError();

        if (body) {
            body.scrollTop = 0;
        }

        modal.dispatchEvent(new CustomEvent('wizard:stage', { detail: { stage: name } }));
    }

    modal.querySelectorAll('[data-wizard-goto]').forEach((control) => {
        control.addEventListener('click', () => go(control.dataset.wizardGoto));
    });

    // Closing starts over, so the next open begins at the first stage.
    modal.addEventListener('lk:close', () => go(stages[0]));

    go(stages[0]);

    return { go, current: () => current, showError, clearError };
}

/**
 * One choice among cards: the pressed card is marked and the others released.
 *
 * @param {HTMLElement} group the element holding the .lk-wizard__card buttons
 * @param {(card: HTMLElement) => void} onChoose
 */
export function cards(group, onChoose) {
    const all = Array.from(group.querySelectorAll('.lk-wizard__card'));

    function choose(card) {
        all.forEach((other) => {
            const chosen = other === card;

            other.classList.toggle('is-selected', chosen);
            other.setAttribute('aria-pressed', chosen ? 'true' : 'false');
        });

        onChoose(card);
    }

    all.forEach((card) => card.addEventListener('click', () => choose(card)));

    return { choose };
}
